import React, { useEffect, useMemo, useRef, useState } from "react";
import { MenuRenderer } from "@/components/app/MenuRenderer";

/**
 * Plays the items inside a playlist on rotation.
 * Each item: {kind: 'asset'|'design'|'url', url, asset_kind, design, duration_seconds, start_time, end_time, days}
 * Skips items outside their time window or current day-of-week.
 */
export function PlaylistPlayer({ playlist }) {
  const [idx, setIdx] = useState(0);
  const items = useMemo(() => playlist?.items || [], [playlist]);

  // Filter to "currently eligible" items (within time window + day-of-week).
  const eligible = useMemo(() => {
    if (!items.length) return [];
    const now = new Date();
    const dow = now.getDay(); // 0..6
    const minutes = now.getHours() * 60 + now.getMinutes();
    const parse = (s) => {
      if (!s) return null;
      const [h, m] = s.split(":").map((x) => parseInt(x, 10));
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };
    return items.filter((it) => {
      if (Array.isArray(it.days) && it.days.length && !it.days.includes(dow)) return false;
      const s = parse(it.start_time), e = parse(it.end_time);
      if (s != null && e != null) {
        if (s <= e) return minutes >= s && minutes < e;
        // wrap around midnight
        return minutes >= s || minutes < e;
      }
      return true;
    });
  }, [items]);

  // Auto-advance
  const timer = useRef(null);
  useEffect(() => {
    if (!eligible.length) return;
    const dur = (eligible[idx % eligible.length]?.duration_seconds || 10) * 1000;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setIdx((i) => (i + 1) % eligible.length);
    }, dur);
    return () => clearTimeout(timer.current);
  }, [idx, eligible]);

  // Re-evaluate eligibility every minute so time-windowed items kick in/out
  const recalc = useRef(null);
  useEffect(() => {
    recalc.current = setInterval(() => {
      // Force re-render via setIdx(prev)
      setIdx((i) => i);
    }, 60_000);
    return () => clearInterval(recalc.current);
  }, []);

  if (!eligible.length) {
    return (
      <div className="tv-screen flex items-center justify-center text-white/60 text-xl">
        No items scheduled to play right now
      </div>
    );
  }

  const current = eligible[idx % eligible.length];
  return <PlaylistItemRenderer item={current} />;
}

function PlaylistItemRenderer({ item }) {
  if (!item) return null;
  if (item.kind === "design" && item.design) {
    return <MenuRenderer config={item.design.config} scale="tv" />;
  }
  if (item.kind === "asset" && item.url) {
    if (item.asset_kind === "video") {
      return (
        <video
          src={item.url}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover"
          // Auto-advance is handled by the parent timer; we still loop short videos
          loop
        />
      );
    }
    return (
      <img src={item.url} alt={item.name || ""} className="w-full h-full object-cover" />
    );
  }
  if (item.kind === "url" && item.url) {
    // YouTube special-case: convert watch URLs to embed
    let src = item.url;
    const yt = src.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (yt) {
      src = `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&controls=0&rel=0&playlist=${yt[1]}`;
    }
    return (
      <iframe
        src={src}
        title={item.name || "Playlist item"}
        className="w-full h-full"
        frameBorder="0"
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
      />
    );
  }
  return null;
}
